import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  TablePagination,
} from '@mui/material';
import { useRelatoriosDashService } from '../../../../../../use-cases/relatorios-dash';
import { useAppSelector, useAppDispatch } from '../../../../../../presentation/hooks/store-hooks';
import { setRelatorioReceita } from '../../../../../../application/states/relatorios-dash';
import { IRelatorioReceitaRequest, FileType } from '../../../../../../application/models/IRelatorioDash';
import { ITipoUsuario, TipoPlano, TipoPlanoLabel } from '../../../../../../application/models';
import { formatarNumero } from '../../../../../../utils/formatters';

const tipoPlanoLabel: Record<TipoPlano, string> = {
  [TipoPlano.PrePago]: TipoPlanoLabel.PrePago,
  [TipoPlano.PosPago]: TipoPlanoLabel.PosPago,
  [TipoPlano.FeeMensal]: TipoPlanoLabel.FeeMensal,
  [TipoPlano.POC]: TipoPlanoLabel.POC
};

const INITIAL_FORM_STATE: IRelatorioReceitaRequest = {
  MesReferencia: '',
  AnoReferencia: '',
  generateFiles: false,
  format: FileType.XLSX,
  customTitle: '',
  customSubtitle: '',
  includeTotal: false,
  totalLabel: '',
  sheetName: ''
};

export const RelatoriosReceitas = () => {
  const [formData, setFormData] = useState<IRelatorioReceitaRequest>(INITIAL_FORM_STATE);

  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estados para download
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

  const dispatch = useAppDispatch();
  const { getRelatorioReceita, downloadArquivoRelatorio } = useRelatoriosDashService();
  const { receita, loading, error } = useAppSelector((state: any) => state.relatoriosDash);
  const { currentUser } = useAppSelector((state: any) => state.user);

  // Resetar todo o state ao abrir a página
  useEffect(() => {
    setFormData(INITIAL_FORM_STATE);
    setPage(0);
    setRowsPerPage(10);
    setDownloadingFiles(new Set());
    dispatch(setRelatorioReceita(null));
  }, [dispatch]);

  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS;
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO;

  if (!isDemais && !isTecnologiaDaInformacao) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Resetar paginação para nova consulta
    setPage(0);
    
    try {
      // Sempre enviar includeTotal como true
      await getRelatorioReceita({
        ...formData,
        includeTotal: true
      });
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    }
  };

  const handleInputChange = (field: keyof IRelatorioReceitaRequest, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Funções de paginação
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para obter dados paginados
  const getPaginatedData = () => {
    if (!receita?.data?.data) return [];
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return receita.data.data.slice(startIndex, endIndex);
  };

  // Função para download de arquivo com loading
  const handleDownloadFile = async (file: any) => {
    if (!file.downloadUrl) return;

    setDownloadingFiles(prev => new Set(prev).add(file.filename));

    try {
      await downloadArquivoRelatorio(file);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      alert(`Erro ao baixar arquivo ${file.filename}: ${error}`);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.filename);
        return newSet;
      });
    }
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  const anos = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Relatório de Receitas
      </Typography>
      
      {/* Informativo sobre disponibilidade de dados e permissões */}
      <Alert severity="info" sx={{ mb: 3 }}>

        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Permissões:</strong> Este relatório é exclusivo para usuários VELES DEMAIS.
        </Typography>
      </Alert>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel>Mês de Referência</InputLabel>
                  <Select
                    value={formData.MesReferencia}
                    onChange={(e) => handleInputChange('MesReferencia', e.target.value)}
                  >
                    {meses.map((mes) => (
                      <MenuItem key={mes.value} value={mes.value}>
                        {mes.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel>Ano de Referência</InputLabel>
                  <Select
                    value={formData.AnoReferencia}
                    onChange={(e) => handleInputChange('AnoReferencia', e.target.value)}
                  >
                    {anos.map((ano) => (
                      <MenuItem key={ano} value={ano}>
                        {ano}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Formato</InputLabel>
                  <Select
                    value={formData.format}
                    onChange={(e) => handleInputChange('format', e.target.value)}
                  >
                    <MenuItem value="xlsx">Excel</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                    <MenuItem value="txt">TXT</MenuItem>
                    <MenuItem value="all">Todos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Opções de Geração de Arquivos
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.generateFiles}
                        onChange={(e) => handleInputChange('generateFiles', e.target.checked)}
                      />
                    }
                    label="Gerar arquivos de exportação"
                  />
                </FormGroup>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !formData.MesReferencia || !formData.AnoReferencia}
                  >
                    {loading ? 'Gerando...' : 'Gerar Relatório'}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => setFormData({
                      MesReferencia: 0,
                      AnoReferencia: 0,
                      generateFiles: false,
                      format: FileType.XLSX,
                      customTitle: '',
                      customSubtitle: '',
                      includeTotal: false,
                      totalLabel: ''
                    })}
                  >
                    Limpar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {receita && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resultado do Relatório
            </Typography>
            
 
            {/* Informações do relatório */}
            {receita.data && receita.data.count && receita.data.count.length > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total de registros encontrados:</strong> {formatarNumero(receita.data.count[0]?.RowNumber || 0)}
                </Typography>
              </Box>
            )}
            
            {receita.data && receita.data.data && receita.data.data.length > 0 ? (
              <>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Cliente</strong></TableCell>
                        <TableCell><strong>CNPJ</strong></TableCell>
                        <TableCell><strong>Mês/Ano</strong></TableCell>
                        <TableCell><strong>Tipo Plano</strong></TableCell>
                        <TableCell><strong>Quantidade</strong></TableCell>
                        <TableCell><strong>Valor Faturado</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getPaginatedData().map((row: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {row.NomeFantasia || row.RazaoSocial}
                              </Typography>

                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {row.CNPJ}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.Mes}/{row.Ano}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={tipoPlanoLabel[row.TipoPlano as TipoPlano] || row.TipoPlano || 'N/A'} 
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatarNumero(row.QuantidadeConsumida) || '0'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {row.ValorFaturado ? 
                                `R$ ${(row.ValorFaturado / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 
                                'R$ 0,00'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={row.AssinaturaAtiva ? 'Ativa' : 'Inativa'} 
                              color={row.AssinaturaAtiva ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Paginação */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={receita.data.data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${formatarNumero(from)}-${formatarNumero(to)} de ${count !== -1 ? formatarNumero(count) : `mais de ${formatarNumero(to)}`}`
                  }
                />
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Nenhum dado encontrado para os filtros selecionados.
              </Typography>
            )}
            
            {/* Arquivos gerados - se existirem */}
            {receita.data && receita.data.files && receita.data.files.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  📁 Arquivos Gerados
                </Typography>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>✅ Relatório gerado com sucesso!</strong><br/>
                    {receita.data.message || `${formatarNumero(receita.data.files.length)} arquivo(s) gerado(s)`}<br/>
                    <em>💡 As informações completas estão disponíveis nos arquivos para download abaixo.</em>
                    {downloadingFiles.size > 0 && (
                      <><br/><strong>⏳ {downloadingFiles.size} arquivo(s) sendo baixado(s)...</strong></>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {receita.data.files.map((file: any, index: number) => {
                    const isDownloading = downloadingFiles.has(file.filename);
                    return (
                      <Chip
                        key={index}
                        label={
                          isDownloading 
                            ? `⏳ Baixando ${file.filename}...` 
                            : `📄 ${file.filename} (${file.type.toUpperCase()}) - ${file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}`
                        }
                        color={isDownloading ? "default" : "primary"}
                        variant="outlined"
                        disabled={isDownloading}
                        sx={{ 
                          mr: 1, 
                          mb: 1,
                          opacity: isDownloading ? 0.7 : 1,
                          '&:hover': !isDownloading ? {
                            backgroundColor: 'primary.main',
                            color: 'white'
                          } : {},
                          '&:disabled': {
                            opacity: 0.7
                          }
                        }}
                        onClick={() => {
                          if (!isDownloading && file.downloadUrl) {
                            handleDownloadFile(file);
                          }
                        }}
                        style={{ 
                          cursor: !isDownloading && file.downloadUrl ? 'pointer' : 'default' 
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
